import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx-js-style';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { formatCurrency, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useLoanMappings } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
import type { LoanMapping, MappedGL, LoanMappingReportGl } from 'api/types/loanMapping.types';
import { loanMappingService } from 'api/services/loanMappingService';

// ============================================================================
// TYPES
// ============================================================================

interface MappingRow {
  id: string;
  branchDisplay: string;
  businessUnitCode: string;
  businessUnitName: string;
  customerId: string;
  customerName: string;
  addonLoanType: string;
  addonLoanAccountNumber: string;
  addonLoanDate: string;
  approvedAmount: number;
  statusCode: string;
  approvedAt: string;
  createdAt: string;
  glCount: number;
  glNumbers: string;
  glCustomerIds: string;
  glCustomerNames: string;
  totalNetWeight: number;
  totalGoldLoanAmount: number;
}

function toMappingRow(m: LoanMapping): MappingRow {
  const code = m.businessUnitCode ?? '';
  const name = m.businessUnitName ?? '';
  const branchDisplay = code && name ? `${code} \u2013 ${name}` : name || code;
  return {
    id: String(m.id),
    branchDisplay,
    businessUnitCode: code,
    businessUnitName: name,
    customerId: m.customerId ?? '',
    customerName: m.customerName ?? '',
    addonLoanType: m.addonLoanType ?? '',
    addonLoanAccountNumber: m.addonLoanAccountNumber ?? '',
    addonLoanDate: m.addonLoanDate ?? '',
    approvedAmount: m.approvedAmount,
    statusCode: m.statusCode ?? '',
    approvedAt: m.approvedAt ?? '',
    createdAt: m.createdAt ?? '',
    glCount: m.glCount ?? 0,
    glNumbers: m.glNumbers ?? '',
    glCustomerIds: m.glCustomerIds ?? '',
    glCustomerNames: m.glCustomerNames ?? '',
    totalNetWeight: m.totalNetWeight ?? 0,
    totalGoldLoanAmount: m.totalGoldLoanAmount ?? 0,
  };
}

function fmtDate(val?: string | null): string {
  if (!val) return '';
  return val.split('T')[0];
}

// ============================================================================
// EXCEL UTILITIES
// ============================================================================

export interface ExportFilter { label: string; value: string; }

// ── Border tokens ─────────────────────────────────────────────────────────
const THIN_BORDER   = { style: 'thin', color: { rgb: 'D0D7E2' } } as const;
const HEADER_BORDER = { style: 'thin', color: { rgb: '15294A' } } as const;

// ── Shared banner fill (used across all header-band cells) ────────────────
const BANNER_FILL = { fgColor: { rgb: 'DCE6F1' }, patternType: 'solid' } as const;
const FILTER_FILL = { fgColor: { rgb: 'F7F9FC' }, patternType: 'solid' } as const;

// ── Cell styles ───────────────────────────────────────────────────────────

// Banner row 0 — report title (centered, merged)
const BANNER_TITLE_STYLE = {
  font: { bold: true, sz: 14, name: 'Calibri', color: { rgb: '1E3A5F' } },
  fill: BANNER_FILL,
  alignment: { horizontal: 'center', vertical: 'center' },
};

// Banner row 1 — export date (centered, merged)
const BANNER_DATE_STYLE = {
  font: { italic: true, sz: 10, name: 'Calibri', color: { rgb: '6E7C8C' } },
  fill: BANNER_FILL,
  alignment: { horizontal: 'center', vertical: 'center' },
};

// Filter section label (e.g. "Applied Filters:")
const FILTER_SECTION_STYLE = {
  font: { bold: true, sz: 10, name: 'Calibri', color: { rgb: '1E3A5F' } },
  fill: { fgColor: { rgb: 'EEF3FA' }, patternType: 'solid' },
  alignment: { horizontal: 'left', vertical: 'center' },
};

// Filter key cell (e.g. "Branch:")
const FILTER_LABEL_STYLE = {
  font: { bold: true, sz: 10, name: 'Calibri', color: { rgb: '444444' } },
  fill: FILTER_FILL,
  alignment: { horizontal: 'left', vertical: 'center' },
};

// Filter value cell
const FILTER_VALUE_STYLE = {
  font: { sz: 10, name: 'Calibri', color: { rgb: '222222' } },
  fill: FILTER_FILL,
  alignment: { horizontal: 'left', vertical: 'center' },
};

// Column header row
const HEADER_STYLE = {
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11, name: 'Calibri' },
  fill: { fgColor: { rgb: '1E3A5F' }, patternType: 'solid' },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: false },
  border: { top: HEADER_BORDER, bottom: HEADER_BORDER, left: HEADER_BORDER, right: HEADER_BORDER },
};

// Data cell styles
const DATA_STYLE = {
  font: { sz: 10, name: 'Calibri' },
  alignment: { vertical: 'center' },
  border: { top: THIN_BORDER, bottom: THIN_BORDER, left: THIN_BORDER, right: THIN_BORDER },
};

const NUMBER_STYLE = {
  font: { sz: 10, name: 'Calibri' },
  alignment: { vertical: 'center', horizontal: 'right' },
  border: { top: THIN_BORDER, bottom: THIN_BORDER, left: THIN_BORDER, right: THIN_BORDER },
};

/**
 * Build a styled XLSX workbook.
 *
 * Header band (compact, no blank rows):
 *   Row 0  — [Report Title ← left]  [spacer…]  [Exported on: {date} → right]
 *   Row 1  — "Applied Filters:" label  (only when activeFilters is non-empty)
 *   Row 2+ — one row per active filter: [Label:] [Value]  (only when filters present)
 *
 * Data band:
 *   Row N   — column headers (bold, navy)
 *   Row N+1+— data rows
 *
 * To add future metadata (e.g. "Generated by:", "Period:") pass additional
 * ExportFilter entries in activeFilters, or extend the RowKind union.
 */
function buildStyledWorkbook(
  sheetName: string,
  rows: Record<string, unknown>[],
  numericColumns: string[] = [],
  activeFilters: ExportFilter[] = [],
): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();

  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  const colCount = Math.max(headers.length, 2);

  const empty = (n: number) => Array(Math.max(n, 0)).fill('');

  const aoa: unknown[][] = [];
  const rowHeights: { hpx: number }[] = [];
  const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];

  type RowKind = 'bannerTitle' | 'bannerDate' | 'filterSection' | 'filterRow' | 'colHeader' | 'data';
  const rowKinds: RowKind[] = [];

  const exportDate = new Date().toLocaleDateString('en-IN', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  // ── Row 0: report title — merged, centred ─────────────────────────────────
  aoa.push(['Loan Mapping Report', ...empty(colCount - 1)]);
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });
  rowHeights.push({ hpx: 28 });
  rowKinds.push('bannerTitle');

  // ── Row 1: export date — merged, centred ──────────────────────────────────
  aoa.push([`Exported on: ${exportDate}`, ...empty(colCount - 1)]);
  merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } });
  rowHeights.push({ hpx: 20 });
  rowKinds.push('bannerDate');

  // ── Filter section (immediately after banner, no blank separator) ─────────
  if (activeFilters.length > 0) {
    // "Applied Filters:" label — merged across all columns
    aoa.push(['Applied Filters:', ...empty(colCount - 1)]);
    merges.push({ s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: colCount - 1 } });
    rowHeights.push({ hpx: 20 });
    rowKinds.push('filterSection');

    // One row per active filter
    for (const f of activeFilters) {
      aoa.push([`${f.label}:`, f.value, ...empty(colCount - 2)]);
      rowHeights.push({ hpx: 18 });
      rowKinds.push('filterRow');
    }
  }

  // ── Column header row (directly after metadata, no blank separator) ───────
  const colHeaderRowIdx = aoa.length;
  aoa.push(headers.length > 0 ? headers : empty(colCount));
  rowHeights.push({ hpx: 22 });
  rowKinds.push('colHeader');

  // ── Data rows ─────────────────────────────────────────────────────────────
  for (const row of rows) {
    aoa.push(headers.map((h) => row[h] ?? ''));
    rowHeights.push({ hpx: 18 });
    rowKinds.push('data');
  }
  if (rows.length === 0) {
    aoa.push(empty(colCount));
    rowHeights.push({ hpx: 18 });
    rowKinds.push('data');
  }

  // ── Build worksheet ───────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!merges'] = merges;
  ws['!rows'] = rowHeights;

  // Auto-fit column widths (based on header text + data content)
  if (headers.length > 0) {
    ws['!cols'] = headers.map((header) => {
      const maxLen = Math.max(
        header.length,
        ...rows.map((row) => String(row[header] ?? '').length),
      );
      return { wch: Math.min(maxLen + 4, 55) };
    });
  }

  // ── Apply cell styles ─────────────────────────────────────────────────────
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');

  for (let R = range.s.r; R <= range.e.r; R++) {
    const kind = rowKinds[R];
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });

      if (kind === 'bannerTitle' || kind === 'bannerDate') {
        // All cells in merged banner rows need the fill so the band spans the
        // full width; only the first cell (C === 0) carries content + full style.
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = C === 0
          ? (kind === 'bannerTitle' ? BANNER_TITLE_STYLE : BANNER_DATE_STYLE)
          : { fill: BANNER_FILL };
        continue;
      }

      if (!ws[addr]) continue;

      switch (kind) {
        case 'filterSection':
          ws[addr].s = FILTER_SECTION_STYLE;
          break;
        case 'filterRow':
          ws[addr].s = C === 0 ? FILTER_LABEL_STYLE : FILTER_VALUE_STYLE;
          break;
        case 'colHeader':
          ws[addr].s = HEADER_STYLE;
          break;
        case 'data':
          if (R > colHeaderRowIdx) {
            ws[addr].s = numericColumns.includes(headers[C]) ? NUMBER_STYLE : DATA_STYLE;
          }
          break;
        default: break;
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return wb;
}

// ============================================================================
// MAIN SCREEN
// ============================================================================

const LoanMappingReportScreen: React.FC = () => {
  const { showMessage } = useSnackbar();
  const { data, loading: apiLoading, error: apiError, refetch } = useLoanMappings();

  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterLoanAccount, setFilterLoanAccount] = useState('');
  const [filterGlNumber, setFilterGlNumber] = useState('');
  const [filterGlCustomerId, setFilterGlCustomerId] = useState('');
  const [filterGlCustomerName, setFilterGlCustomerName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // GL detail panel
  const [expandedMappingId, setExpandedMappingId] = useState<string | null>(null);
  const glCacheRef = useRef<Map<string, MappedGL[]>>(new Map());
  const [glPanelRows, setGlPanelRows] = useState<MappedGL[]>([]);
  const [glPanelLoading, setGlPanelLoading] = useState(false);

  // Export dropdown
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportSummaryLoading, setExportSummaryLoading] = useState(false);
  const [exportDetailLoading, setExportDetailLoading] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Close export menu on outside click
  useEffect(() => {
    if (!exportMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportMenuOpen]);

  // ============================================================================
  // MAPPING ROWS + FILTER
  // ============================================================================

  const mappingRows = useMemo(() => data.map(toMappingRow), [data]);

  const filteredData = useMemo(() => {
    return mappingRows.filter((row) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const hay = `${row.customerName} ${row.customerId} ${row.branchDisplay} ${row.addonLoanAccountNumber} ${row.addonLoanType} ${row.statusCode}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterStatus && row.statusCode !== filterStatus) return false;
      if (filterBranch) {
        const fb = filterBranch.toLowerCase();
        if (!row.businessUnitName.toLowerCase().includes(fb) && !row.businessUnitCode.toLowerCase().includes(fb)) return false;
      }
      if (filterCustomerId && !row.customerId.toLowerCase().includes(filterCustomerId.toLowerCase())) return false;
      if (filterLoanAccount && !row.addonLoanAccountNumber.toLowerCase().includes(filterLoanAccount.toLowerCase())) return false;
      if (filterGlNumber && !(row.glNumbers || '').toLowerCase().includes(filterGlNumber.toLowerCase())) return false;
      if (filterGlCustomerId && !(row.glCustomerIds || '').toLowerCase().includes(filterGlCustomerId.toLowerCase())) return false;
      if (filterGlCustomerName && !(row.glCustomerNames || '').toLowerCase().includes(filterGlCustomerName.toLowerCase())) return false;
      if (dateFrom) {
        const rowDate = (row.approvedAt || row.createdAt || '').split('T')[0];
        if (rowDate < dateFrom) return false;
      }
      if (dateTo) {
        const rowDate = (row.approvedAt || row.createdAt || '').split('T')[0];
        if (rowDate > dateTo) return false;
      }
      return true;
    });
  }, [mappingRows, searchQuery, filterStatus, filterBranch, filterCustomerId, filterLoanAccount, filterGlNumber, filterGlCustomerId, filterGlCustomerName, dateFrom, dateTo]);

  const isFilterActive = !!(filterStatus || filterBranch || filterCustomerId || filterLoanAccount || filterGlNumber || filterGlCustomerId || filterGlCustomerName || dateFrom || dateTo);

  // Build the list of active filters for the Excel report header
  const getActiveFilters = useCallback((): ExportFilter[] => {
    const list: ExportFilter[] = [];
    if (filterBranch)         list.push({ label: 'Branch',           value: filterBranch });
    if (filterCustomerId)     list.push({ label: 'Customer ID',      value: filterCustomerId });
    if (filterLoanAccount)    list.push({ label: 'Loan Account',     value: filterLoanAccount });
    if (filterGlNumber)       list.push({ label: 'GL Account No.',   value: filterGlNumber });
    if (filterGlCustomerId)   list.push({ label: 'GL Customer ID',   value: filterGlCustomerId });
    if (filterGlCustomerName) list.push({ label: 'GL Customer Name', value: filterGlCustomerName });
    if (filterStatus)         list.push({ label: 'Status',           value: filterStatus.replace(/_/g, ' ') });
    if (dateFrom)             list.push({ label: 'Date From',        value: dateFrom });
    if (dateTo)               list.push({ label: 'Date To',          value: dateTo });
    if (searchQuery)          list.push({ label: 'Search',           value: searchQuery });
    return list;
  }, [filterBranch, filterCustomerId, filterLoanAccount, filterGlNumber, filterGlCustomerId, filterGlCustomerName, filterStatus, dateFrom, dateTo, searchQuery]);

  // ============================================================================
  // GL COUNT CLICK — lazy-load + cache
  // ============================================================================

  const handleGlCountClick = useCallback(async (mappingId: string) => {
    if (expandedMappingId === mappingId) {
      setExpandedMappingId(null);
      return;
    }
    setExpandedMappingId(mappingId);
    if (glCacheRef.current.has(mappingId)) {
      setGlPanelRows(glCacheRef.current.get(mappingId)!);
      return;
    }
    setGlPanelLoading(true);
    try {
      const res = await loanMappingService.getById(mappingId);
      const gls = res.data?.mappedGls ?? [];
      glCacheRef.current.set(mappingId, gls);
      setGlPanelRows(gls);
    } catch {
      showMessage('Failed to load GL details.', 'error');
      setExpandedMappingId(null);
    } finally {
      setGlPanelLoading(false);
    }
  }, [expandedMappingId, showMessage]);

  // ============================================================================
  // EXPORT SUMMARY
  // ============================================================================

  const handleExportSummary = useCallback(async () => {
    setExportMenuOpen(false);
    if (filteredData.length === 0) { showMessage('No data to export.', 'info'); return; }
    setExportSummaryLoading(true);
    showMessage('Preparing Summary export…', 'info');
    try {
      const NUMERIC = ['Approved Amt (₹)', 'GL Count', 'Eligible Weight (g)', 'Original GL Amt (₹)'];
      const rows = filteredData.map((row) => ({
        'Branch': row.branchDisplay,
        'Customer ID': row.customerId,
        'Customer Name': row.customerName,
        'Loan Type': row.addonLoanType,
        'Loan Account': row.addonLoanAccountNumber,
        'Loan Date': fmtDate(row.addonLoanDate),
        'Approved Amt (₹)': row.approvedAmount,
        'Status': row.statusCode.replace(/_/g, ' '),
        'GL Count': row.glCount,
        'Eligible Weight (g)': +Number(row.totalNetWeight).toFixed(3),
        'Original GL Amt (₹)': row.totalGoldLoanAmount,
      }));
      const wb = buildStyledWorkbook('Summary', rows, NUMERIC, getActiveFilters());
      XLSX.writeFile(wb, `loan-mapping-summary-${new Date().toISOString().split('T')[0]}.xlsx`);
      showMessage(`Summary exported — ${rows.length} row${rows.length !== 1 ? 's' : ''}.`, 'success');
    } catch {
      showMessage('Summary export failed. Please try again.', 'error');
    } finally {
      setExportSummaryLoading(false);
    }
  }, [filteredData, showMessage, getActiveFilters]);

  // ============================================================================
  // EXPORT DETAIL
  // ============================================================================

  const handleExportDetail = useCallback(async () => {
    setExportMenuOpen(false);
    if (filteredData.length === 0) { showMessage('No data to export.', 'info'); return; }
    setExportDetailLoading(true);
    showMessage('Fetching GL data for Detail export…', 'info');
    try {
      // One bulk call replaces the previous N+1 concurrent getById fan-out.
      // The backend returns every matching mapping with its GLs nested — no per-mapping HTTP calls,
      // no silent failures from browser connection-limit or gateway timeouts.
      const bulk = await loanMappingService.getReportDetail();
      const bulkList = bulk.data ?? [];
      const glMap = new Map<string, LoanMappingReportGl[]>();
      for (const m of bulkList) {
        glMap.set(String(m.id), m.mappedGls ?? []);
      }

      const NUMERIC = ['Approved Amt (₹)', 'Eligible Weight (g)', 'Original GL Amt (₹)', 'Alloc. Ratio (%)'];
      const detailRows: Record<string, unknown>[] = [];

      for (const row of filteredData) {
        const gls = glMap.get(row.id) ?? [];
        const base = {
          'Branch': row.branchDisplay,
          'Customer ID': row.customerId,
          'Customer Name': row.customerName,
          'Loan Type': row.addonLoanType,
          'Loan Account': row.addonLoanAccountNumber,
          'Loan Date': fmtDate(row.addonLoanDate),
          'Approved Amt (₹)': row.approvedAmount,
          'Status': row.statusCode.replace(/_/g, ' '),
        };
        if (gls.length === 0) {
          detailRows.push({
            ...base,
            'GL Account Number': '',
            'GL Date': '',
            'GL Customer ID': '',
            'GL Customer Name': '',
            'Eligible Weight (g)': '',
            'Original GL Amt (₹)': '',
            'Alloc. Ratio (%)': '',
            'GL Status': '',
          });
        } else {
          for (const gl of gls) {
            detailRows.push({
              ...base,
              'GL Account Number': gl.glNumber ?? '',
              'GL Date': fmtDate(gl.loanDate),
              'GL Customer ID': gl.glCustomerId ?? '',
              'GL Customer Name': gl.glCustomerName ?? '',
              'Eligible Weight (g)': +Number(gl.netWeight).toFixed(3),
              'Original GL Amt (₹)': gl.originalLoanAmount,
              'Alloc. Ratio (%)': gl.allocationRatio != null
                ? +((Number(gl.allocationRatio) * 100).toFixed(4))
                : '',
              'GL Status': (gl.statusCode ?? '').replace(/_/g, ' '),
            });
          }
        }
      }

      const wb = buildStyledWorkbook('Detailed', detailRows, NUMERIC, getActiveFilters());
      XLSX.writeFile(wb, `loan-mapping-detailed-${new Date().toISOString().split('T')[0]}.xlsx`);
      showMessage(`Detail exported — ${detailRows.length} GL row${detailRows.length !== 1 ? 's' : ''}.`, 'success');
    } catch {
      showMessage('Detail export failed. Please try again.', 'error');
    } finally {
      setExportDetailLoading(false);
    }
  }, [filteredData, showMessage, getActiveFilters]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<MappingRow>[] = useMemo(() => [
    {
      id: 'branchDisplay',
      header: 'Branch',
      accessorKey: 'branchDisplay',
      size: 180,
    },
    {
      id: 'customerId',
      header: 'Customer ID',
      accessorKey: 'customerId',
      size: 130,
    },
    {
      id: 'customerName',
      header: 'Customer Name',
      accessorKey: 'customerName',
      size: 180,
    },
    {
      id: 'addonLoanType',
      header: 'Loan Type',
      accessorKey: 'addonLoanType',
      size: 130,
    },
    {
      id: 'addonLoanAccountNumber',
      header: 'Loan Account',
      accessorKey: 'addonLoanAccountNumber',
      size: 160,
    },
    {
      id: 'addonLoanDate',
      header: 'Loan Date',
      accessorKey: 'addonLoanDate',
      size: 120,
      cell: ({ getValue }: any) => fmtDate(getValue()) || '—',
    },
    {
      id: 'approvedAmount',
      header: 'Approved Amt (₹)',
      accessorKey: 'approvedAmount',
      size: 155,
      cell: ({ getValue }: any) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>
      ),
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 140,
      cell: ({ getValue }: any) => (
        <span style={getStatusBadgeStyle(getValue())}>{(getValue() ?? '').replace(/_/g, ' ')}</span>
      ),
    },
    {
      id: 'glCount',
      header: 'GL Count',
      accessorKey: 'glCount',
      size: 100,
      cell: ({ getValue, row }: any) => {
        const count = getValue() as number;
        const rowId = (row.original as MappingRow).id;
        const isExpanded = expandedMappingId === rowId;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleGlCountClick(rowId);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              height: 24,
              padding: '0 8px',
              borderRadius: 12,
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 700,
              background: isExpanded ? 'var(--primary)' : 'var(--primary-light, #e8f0fe)',
              color: isExpanded ? '#fff' : 'var(--primary)',
              transition: 'background 0.15s, color 0.15s',
            }}
            title={isExpanded ? 'Collapse GL details' : 'View GL details'}
          >
            {count}
          </button>
        );
      },
    },
    {
      id: 'totalNetWeight',
      header: 'Eligible Weight (g)',
      accessorKey: 'totalNetWeight',
      size: 130,
      cell: ({ getValue }: any) => {
        const v = getValue() as number;
        return v > 0 ? <span style={{ fontWeight: 600 }}>{Number(v).toFixed(3)}</span> : '—';
      },
    },
    {
      id: 'totalGoldLoanAmount',
      header: 'Original GL Amt (₹)',
      accessorKey: 'totalGoldLoanAmount',
      size: 170,
      cell: ({ getValue }: any) => {
        const v = getValue() as number;
        return v > 0 ? <span style={{ fontWeight: 600 }}>{formatCurrency(v)}</span> : '—';
      },
    },
  ], [expandedMappingId, handleGlCountClick]);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const isExporting = exportSummaryLoading || exportDetailLoading;

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      variant: 'icon',
      tooltip: 'Refresh',
      onClick: () => refetch(),
    },
    {
      id: 'export',
      icon: <IconSystem.download size={16} />,
      label: '',
      variant: 'icon',
      tooltip: isExporting ? 'Exporting…' : 'Export',
      onClick: () => { if (!isExporting) setExportMenuOpen((prev) => !prev); },
      disabled: isExporting,
    },
  ];

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterBranch('');
    setFilterCustomerId('');
    setFilterLoanAccount('');
    setFilterGlNumber('');
    setFilterGlCustomerId('');
    setFilterGlCustomerName('');
    setDateFrom('');
    setDateTo('');
  };

  const expandedRow = expandedMappingId ? filteredData.find((r) => r.id === expandedMappingId) : null;

  // Shared dropdown item style builder
  const dropdownItemStyle = (loading: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    background: 'transparent',
    cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: '0.85rem',
    color: loading ? 'var(--text-disabled, #aaa)' : 'var(--text-primary)',
    textAlign: 'left',
  });

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container" style={{ position: 'relative' }}>
      <ModuleHeader
        visible={true}
        title="Loan Mapping Report"
        icon={<IconSystem.list size={20} />}
        description="Mapping details with GL allocation breakdown and eligibility calculations."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search mappings...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: isFilterActive }}
        actions={actionButtons}
      />

      {/* Export dropdown menu */}
      {exportMenuOpen && (
        <div
          ref={exportMenuRef}
          style={{
            position: 'absolute',
            top: 52,
            right: 12,
            zIndex: 400,
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #e0e0e0)',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
            minWidth: 168,
            overflow: 'hidden',
          }}
        >
          {/* Summary */}
          <button
            onClick={handleExportSummary}
            disabled={exportSummaryLoading}
            style={dropdownItemStyle(exportSummaryLoading)}
            onMouseEnter={(e) => { if (!exportSummaryLoading) (e.currentTarget).style.background = 'var(--hover-bg, #f5f7fa)'; }}
            onMouseLeave={(e) => { (e.currentTarget).style.background = 'transparent'; }}
          >
            {exportSummaryLoading
              ? <ExportSpinner />
              : <IconSystem.download size={14} />
            }
            {exportSummaryLoading ? 'Exporting…' : 'Summary'}
          </button>

          <div style={{ height: 1, background: 'var(--border-light, #f0f0f0)', margin: '0 12px' }} />

          {/* Detail */}
          <button
            onClick={handleExportDetail}
            disabled={exportDetailLoading}
            style={dropdownItemStyle(exportDetailLoading)}
            onMouseEnter={(e) => { if (!exportDetailLoading) (e.currentTarget).style.background = 'var(--hover-bg, #f5f7fa)'; }}
            onMouseLeave={(e) => { (e.currentTarget).style.background = 'transparent'; }}
          >
            {exportDetailLoading
              ? <ExportSpinner />
              : <IconSystem.download size={14} />
            }
            {exportDetailLoading ? 'Fetching GLs…' : 'Detail'}
          </button>
        </div>
      )}

      <div className="module-content">
        {apiError && data.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load report data</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && data.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

            {/* Grid wrapper — shrinks to make room for GL panel */}
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                enableRowSelection={false}
                enablePagination
                enableColumnFilters={false}
                emptyMessage="No loan mappings match the current filters."
              />
            </div>

            {/* GL Detail Panel — fixed height, does not shrink */}
            {expandedMappingId && (
              <div style={{
                flexShrink: 0,
                borderTop: '2px solid var(--primary)',
                background: 'var(--surface, #fff)',
                padding: '0.75rem 1rem',
                maxHeight: 260,
                overflow: 'auto',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)' }}>GL Details</span>
                    {expandedRow && (
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                        {'\u2014'} {expandedRow.customerName}
                        {expandedRow.addonLoanAccountNumber ? ` (${expandedRow.addonLoanAccountNumber})` : ''}
                      </span>
                    )}
                  </div>
                  <button
                    className="search-panel-close"
                    onClick={() => setExpandedMappingId(null)}
                    title="Close GL details"
                  >
                    <IconSystem.x size={14} />
                  </button>
                </div>

                {glPanelLoading ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    Loading GL details…
                  </div>
                ) : glPanelRows.length === 0 ? (
                  <div style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    No GL records found for this mapping.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--table-header-bg, #f4f6f9)' }}>
                        {['GL Account Number', 'GL Date', 'GL Customer ID', 'GL Customer Name', 'Eligible Weight (g)', 'Original Loan Amt (₹)', 'Alloc. Ratio (%)', 'GL Status'].map((h) => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {glPanelRows.map((gl) => (
                        <tr key={gl.id} style={{ borderBottom: '1px solid var(--border-light, #eee)' }}>
                          <td style={{ padding: '6px 10px' }}>{gl.glNumber || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{fmtDate(gl.loanDate) || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{gl.glCustomerId || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{gl.glCustomerName || '—'}</td>
                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>{Number(gl.netWeight).toFixed(3)}</td>
                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>{formatCurrency(gl.originalLoanAmount)}</td>
                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>{(Number(gl.allocationRatio) * 100).toFixed(2)}%</td>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={getStatusBadgeStyle(gl.statusCode ?? '')}>{(gl.statusCode ?? '').replace(/_/g, ' ')}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Status</label>
              <select
                className="form-input"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              >
                <option value="">All</option>
                <option value="OPEN">Open</option>
                <option value="CLOSED">Closed</option>
                <option value="PENDING_APPROVAL">Pending Approval</option>
                <option value="APPROVED">Approved</option>
              </select>
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Branch</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by branch code or name..."
                value={filterBranch}
                onChange={(e) => setFilterBranch(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Customer ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by customer ID..."
                value={filterCustomerId}
                onChange={(e) => setFilterCustomerId(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Loan Account</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by loan account..."
                value={filterLoanAccount}
                onChange={(e) => setFilterLoanAccount(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>GL Account Number</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by GL number..."
                value={filterGlNumber}
                onChange={(e) => setFilterGlNumber(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>GL Customer ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by GL customer ID..."
                value={filterGlCustomerId}
                onChange={(e) => setFilterGlCustomerId(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>GL Customer Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by GL customer name..."
                value={filterGlCustomerName}
                onChange={(e) => setFilterGlCustomerName(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SPINNER — animated SVG used inside export dropdown items
// ============================================================================

const ExportSpinner: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    style={{ animation: 'lmms-spin 0.8s linear infinite', flexShrink: 0 }}
  >
    <style>{`@keyframes lmms-spin { to { transform: rotate(360deg); } }`}</style>
    <circle cx="7" cy="7" r="5.5" fill="none" stroke="var(--primary, #1E3A5F)" strokeWidth="2" strokeDasharray="22 10" strokeLinecap="round" />
  </svg>
);

export default LoanMappingReportScreen;
